import { useState } from 'react'

import {
  AvatarStack,
  BordersStyles,
  Box,
  Button,
  Icon,
  Tag,
  Text,
} from '@factorialco/deprecated-design-system'
import { DocumentsUploadProcessItemFragment } from 'generated/resources/graphql'

import Callout from 'modules/core/components/Callout'
import Modal from 'modules/core/components/Modal'
import { useUnsafeMemo } from 'modules/core/hooks/useMemo'
import buildUrl from 'modules/core/lib/buildUrl'
import i18n from 'modules/core/lib/i18n'

import { useManualDocumentAssignerContext } from 'modules/documents/components/BulkFiles/ManualDocumentAssigner/context'

const MAX_VISIBLE_SIGNERS = 3

interface Props {
  uploadProcess: DocumentsUploadProcessItemFragment
  onSuccess: () => void
  onClose: () => void
}

const SummaryModal = ({ uploadProcess, onSuccess, onClose }: Props) => {
  const signatureRequested = uploadProcess?.esignatureRequest ?? false
  const context = useManualDocumentAssignerContext()
  const [leftOver, setLeftOver] = useState<number>(0)

  const signers = useUnsafeMemo(() => {
    const additionalSigners = uploadProcess?.additionalSigners || []

    setLeftOver(additionalSigners.length - MAX_VISIBLE_SIGNERS)
    return additionalSigners?.slice(0, MAX_VISIBLE_SIGNERS).map((signer) => {
      return {
        label: signer.fullName,
        url: buildUrl(signer.avatar ?? ''),
      } as const
    })
  }, [uploadProcess?.additionalSigners])

  const canSeeAdditionalSigners = signers && signers.length > 0
  const border: BordersStyles = signatureRequested
    ? {
        bottom: {
          width: 's1',
          style: 'solid',
          color: 'grey400',
        },
      }
    : {}

  return (
    <Modal
      illustration="file-move"
      title={i18n.t('split_documents.workflow_payslips.resume_modal.title')}
      description={i18n.t('split_documents.workflow_payslips.resume_modal.description')}
      onClose={onClose}
      footer={
        <>
          <Button type="secondary" onClick={onClose}>
            {i18n.t('common.cancel')}
          </Button>
          <Button type="primary" onClick={onSuccess}>
            {i18n.t('split_documents.workflow_payslips.resume_modal.distribute')}
          </Button>
        </>
      }
    >
      <Box
        width="s400"
        gap="s8"
        borderRadius={{ all: 'abs016' }}
        border={{
          all: {
            width: 's1',
            style: 'solid',
            color: 'grey400',
          },
        }}
      >
        <Box
          paddingX="s24"
          paddingY="s16"
          gap="s8"
          flexDirection="row"
          justifyContent="spaceBetween"
          style={{ width: '100%' }}
          border={border}
          alignItems="center"
        >
          <Box style={{ height: '100%' }}>
            <Text size="300" weight="semibold" color="textPrimary">
              {i18n.t('split_documents.workflow_payslips.resume_modal.distribution.title')}
            </Text>
          </Box>
          <Box>
            <Text size="400" weight="semibold" color="grey800">
              {context.payslipsToDistribute}
            </Text>
          </Box>
        </Box>
        {signatureRequested && (
          <Box gap="s8" flexDirection="column" paddingX="s24" paddingY="s12">
            <Box flexDirection="row">
              <Tag type="viridian">
                {i18n.t(
                  'split_documents.workflow_payslips.resume_modal.distribution.signature_requested'
                )}
              </Tag>
            </Box>
            <Box flexDirection="row" alignItems="center">
              <Text size="200" weight="medium" color="textPrimary">
                {i18n.t('split_documents.workflow_payslips.resume_modal.distribution.recipient')}
              </Text>
              <Box alignItems="flexEnd" width="full">
                <Icon.Medium icon="check" color="viridian1200" />
              </Box>
            </Box>
            {canSeeAdditionalSigners && (
              <Box flexDirection="row" alignItems="center">
                <Box flexDirection="row" alignItems="center" width="full-view-height">
                  <Text size="200" weight="medium" color="textPrimary">
                    {i18n.t(
                      'split_documents.workflow_payslips.resume_modal.distribution.additional_signers'
                    )}
                  </Text>
                </Box>
                <Box alignItems="flexEnd">
                  <AvatarStack
                    size="s40"
                    overlap="s12"
                    avatars={signers}
                    leftover={leftOver}
                    textSize={'200'}
                    textColor={'grey000White'}
                    backgroundColor={'champagne1000'}
                  />
                </Box>
              </Box>
            )}
          </Box>
        )}
      </Box>
      <Box paddingTop="s32">
        <Callout
          type="warning"
          description={i18n.t('split_documents.workflow_payslips.resume_modal.alert')}
        ></Callout>
      </Box>
    </Modal>
  )
}

export default SummaryModal
